import React, { useState } from 'react';
import { brandIsNetwork, planLabel } from "../../utils/plans";
import { NavLink, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import { startCase } from 'lodash';
import toast from 'react-hot-toast';
import { Tooltip } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import axiosInstant from '../../api';
import { importBrandVideos } from '../../api/endpoints';
import styles from '../admin/settings/settings.module.css';
import { useUser } from '../../hooks/useUser';
import { canManageRmnNetwork, isInternal } from '../../utils';
import { portalBasePath } from '../../utils/portalPath';
import { ConfirmationDialog } from '../../components/ConfirmationDialog/ConfirmationDialog';

interface Props {
    brandData?: any;
    id?: any;
    name?: any;
}

const BrandDetailMenu: React.FC<Props> = (props) => {
    const { brandData, id } = props;
    const location = useLocation();
    const childPathname = location.pathname.split("/").pop();
    const { role } = useUser();
    const isSales = role === "SALES_TEAM";
    const [showImportConfirm, setShowImportConfirm] = useState(false);
    const [importBrandId, setImportBrandId] = useState<any>(null);

    const handleImport = (brandId: any) => {
        setImportBrandId(brandId);
        setShowImportConfirm(true);
    };

    const confirmImport = async () => {
        setShowImportConfirm(false);
        if (importBrandId) {
            const endpoint = importBrandVideos(importBrandId);
            try {
                await axiosInstant.get(endpoint);
                toast.success("Import finished.");
            } catch (error) {
                toast.error((error as Error).message);
            }
        }
        setImportBrandId(null);
    };

    const getNavLink = (path: string, label?: string) => (
        <NavLink
            to={label && path.startsWith("/") ? path : `/brands/${id}/${path}`}
            className={classNames(styles.navlink, {
                [styles.active]: childPathname === path
            })}
        >
            {label ?? startCase(path)}
        </NavLink>
    );

    return (
        <div
            key={brandData?.id}
            className="p-4 basis-3/12 rounded-lg bg-surface col-span-1 flex flex-col justify-between max-w-[280px]"
        >
            <div className="basis-3/12flex justify-between items-start">
                {brandData?.logo && <img className="w-[72px]" src={brandData?.logo} alt="cover pic" />}

                {!brandData?.logo && <div className="w-[72px] h-[72px] rounded bg-primary bg-opacity-20" />}
            </div>
            <div className="flex flex-col pt-[32px] pb-[32px]">
                <div className="flex justify-end gap-1">
                    {/* Plan chip (matches the brands grid badge): network
                        plans keep the blue outline, other paid plans neutral. */}
                    {brandData?.plan && brandData.plan !== "free" && (
                        <span
                            className={
                                brandIsNetwork(brandData)
                                    ? "font-bold uppercase border border-[#2474DD] bg-surface text-[#2474DD] text-xs rounded-md px-1.5 py-0.5"
                                    : "font-bold uppercase border border-border-token bg-surface-2 text-text-2 text-xs rounded-md px-1.5 py-0.5"
                            }
                        >
                            {planLabel(brandData.plan)}
                        </span>
                    )}
                    {/* Brand-portal workspace — what this brand's users see,
                        opened in-place with a workspace rail + exit. Any
                        brand qualifies (every brand has a slug). */}
                    {brandData?.slug && (
                        <Tooltip title="Open brand portal" arrow placement="top">
                            <NavLink
                                to={`${portalBasePath(brandData.slug)}/dashboard`}
                                className="flex items-center gap-1 font-bold uppercase border border-[#2474DD] bg-surface text-[#2474DD] text-xs rounded-md px-1.5 py-0.5 hover:bg-[#2474DD] hover:text-white"
                            >
                                portal
                                <ArrowForwardIcon sx={{ fontSize: 12 }} />
                            </NavLink>
                        </Tooltip>
                    )}
                </div>
                <Tooltip title={brandData?.name || ""} arrow placement="top">
                    <h2 className="font-[CircularStd] text-text-1 text-4xl uppercase overflow-hidden text-ellipsis whitespace-nowrap">
                        {brandData?.name}
                    </h2>
                </Tooltip>
            </div>
            <div className="" style={{ margin: -16 }}>
                {getNavLink("details")}
                {!isSales && (
                    <div
                        className={`${styles.navlink}`}
                        onClick={() => handleImport(brandData?.id)}
                        style={{ cursor: "pointer" }}
                    >
                        Video Re-Import
                    </div>
                )}
                {getNavLink("team")}
                {getNavLink("products")}
                {getNavLink("experts")}
                {getNavLink(`/videos?brandId=${id}`, "Videos")}
                {getNavLink(`/video-production/projects?tab=active&brandId=${id}`, "Projects")}
                {getNavLink("content-types")}
                {isInternal(role) && getNavLink("file-types")}
                {/* The brand's own Settings tabs, for staff acting on its
                    behalf: manage its public-API keys, hand over the site
                    tag / Shopify pixel install snippets. */}
                {isInternal(role) && getNavLink("api-keys", "Public API Keys")}
                {isInternal(role) && getNavLink("onboarding", "Onboarding")}
                {isInternal(role) && getNavLink("search-console", "Search Console")}
                {/* All brands since TDC-1499 phase 2 (white label / crawl /
                    categories / review); the Network Brands tab inside is
                    still RMN-only. */}
                {canManageRmnNetwork(String(role)) && getNavLink("rmn", "Network")}
            </div>
            <ConfirmationDialog
                open={showImportConfirm}
                onClose={() => {
                    setShowImportConfirm(false);
                    setImportBrandId(null);
                }}
                onConfirm={confirmImport}
                title="Brand Video Re-Import"
                description="Are you sure you want to import all videos for this brand?"
                confirmLabel="Import"
                cancelLabel="Cancel"
            />
        </div>
    );
};

export default BrandDetailMenu;
